<script setup>
import { ref, reactive, computed, onMounted, watch, onActivated } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { useUI } from '@/composables/useUI'
import { usePaginacao } from '@/composables/usePaginacao'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const { obterClasseStatusGhost } = useUI()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

// --- Estado Local ---
const usuarios = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

// Estado para o Modal de Auditoria
const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const listas = reactive({})

const filtros = reactive({
  nome: '',
  email: '',
  idEmpresa: null,
  statusUsuario: ''
})

// --- Permissões (RBAC) ---
const temPermissaoCompleta = computed(() => authStore.pode('USUARIOS_COMPLETO'))
const temPermissaoVisualizacao = computed(() => authStore.pode('USUARIOS_VISUALIZACAO'))

const podeVerAcoes = computed(() => {
  return temPermissaoCompleta.value || temPermissaoVisualizacao.value
})

const sortResolver = (field, order, data) => {
  if (field === 'statusUsuario') {
    return [...data].sort((a, b) => {
      const cmp = getStatusTexto(a.statusUsuario).localeCompare(getStatusTexto(b.statusUsuario), 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }
  return null
}

const colunas = computed(() => {
  const cols = [
    { field: 'idUsuario', label: 'ID', width: 100, sortable: true, slotName: 'id' },
    { field: 'nome', label: 'Nome', sortable: true, class: 'fw-medium' },
    { field: 'apelido', label: 'Apelido', sortable: true },
    { field: 'email', label: 'Email', sortable: true }
  ]
  if (authStore.isAdminInterno) {
    cols.push({ field: 'nomeEmpresa', label: 'Empresa', sortable: true })
  }
  cols.push({ field: 'statusUsuario', label: 'Status', width: 120, align: 'center', sortable: true, slotName: 'status' })
  cols.push({ field: 'aceiteTermoUso', label: 'Termo', width: 80, align: 'center', slotName: 'termo' })
  if (podeVerAcoes.value) {
    cols.push({ field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false })
  }
  return cols
})

const handleSortChange = () => {
  buscarDados(1)
}

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['Usuario'])
    Object.assign(listas, result)
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const getStatusTexto = (idStatus) => {
  if (!idStatus) return '-'
  // Tenta resolver via listas de lookup carregadas (ex: LstStatusUsuario)
  for (const key of Object.keys(listas)) {
    const lista = listas[key]
    if (Array.isArray(lista)) {
      const obj = lista.find(s => s.idListaClienteItem === idStatus || s.idListaSistemaItem === idStatus || s.id === idStatus)
      if (obj) return obj.textoLista || obj.descricao || obj.nome
    }
  }
  // Fallback: se o status já é texto amigável, retorna como está
  return idStatus
}

// --- Watchers Essenciais (Isolamento Multi-Tenant) ---
watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else if (authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  } else {
    usuarios.value = []
    paginacao.value.TotalCount = 0
    Object.keys(listas).forEach(k => delete listas[k])
  }
})

// --- Métodos ---
const buscarDados = async (pageNumber = 1) => {
  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    const empresaParaFiltrar = filtros.idEmpresa || authStore.empresaAtiva

    if (empresaParaFiltrar) {
      params.append('idEmpresa', empresaParaFiltrar)
    } else if (authStore.isAdminInterno) {
      params.append('adminInterno', 'true')
    }

    if (filtros.nome) params.append('nome', filtros.nome)
    if (filtros.email) params.append('email', filtros.email)
    if (filtros.statusUsuario) params.append('statusUsuario', filtros.statusUsuario)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/Usuarios?${params.toString()}`)

    usuarios.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar usuários:', error)
    erro.value = 'Não foi possível carregar os dados dos usuários.'
    usuarios.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.nome = ''
  filtros.email = ''
  filtros.idEmpresa = null
  filtros.statusUsuario = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const adicionarUsuario = () => {
  emit('adicionar-item')
}

const editarUsuario = (usuario) => {
  emit('editar-item', usuario)
}

const abrirAuditoria = (usuario) => {
  auditoriaSelecionada.id = usuario.idUsuario
  auditoriaSelecionada.nome = usuario.nome
  mostrarAuditoria.value = true
}

onMounted(async () => {
  if (authStore.empresaAtiva || authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({
  buscarDados
})
</script>

<template>
  <PaginaAdministrar
    titulo="Usuários"
    subtitulo="Gestão de utilizadores e acessos ao sistema"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="adicionarUsuario"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="temPermissaoCompleta"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i>
        <span>Filtros</span>
      </button>
    </template>

    <template #filtros>
      <h3 class="filtros-titulo">Filtrar por:</h3>
      <div class="row g-3 align-items-end">
        <div class="col-md-3">
          <label for="filtro-nome" class="form-label">Nome</label>
          <input type="text" id="filtro-nome" class="form-control" v-model="filtros.nome" @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label for="filtro-email" class="form-label">Email</label>
          <input type="email" id="filtro-email" class="form-control" v-model="filtros.email" @keydown.enter.prevent="aplicarFiltros">
        </div>

        <div v-if="authStore.isAdminInterno" class="col-md-3">
          <label for="filtro-empresa" class="form-label">ID Empresa</label>
          <input type="number" id="filtro-empresa" class="form-control" v-model.number="filtros.idEmpresa" @keydown.enter.prevent="aplicarFiltros">
        </div>

        <div class="col-md-2">
          <label for="filtro-status" class="form-label">Status</label>
          <select id="filtro-status" class="form-select" v-model="filtros.statusUsuario">
            <option value="">Todos</option>
            <option v-for="s in (listas.LstStatusUsuario || [])" :key="s.idListaClienteItem || s.idListaSistemaItem || s.id" :value="s.idListaClienteItem || s.idListaSistemaItem || s.id">
              {{ s.textoLista || s.descricao || s.nome }}
            </option>
          </select>
        </div>

        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">A carregar...</span>
      </div>
    </div>

    <div v-else-if="erro" class="alert alert-warning m-3 border-0 shadow-sm">
      <i class="bi bi-info-circle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="usuarios"
      :loading="loading"
      empty-message="Nenhum registo encontrado."
      row-key="idUsuario"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarUsuario($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idUsuario }}</span>
      </template>
      <template #status="{ row }">
        <span class="badge" :class="obterClasseStatusGhost(getStatusTexto(row.statusUsuario))">
          {{ getStatusTexto(row.statusUsuario) }}
        </span>
      </template>

      <template #termo="{ row }">
        <span v-if="row.aceiteTermoUso" class="badge badge-ghost-success" title="Termo aceito">
          <i class="bi bi-check-circle"></i>
        </span>
        <span v-else class="badge badge-ghost-secondary" title="Termo não aceito">
          <i class="bi bi-x-circle"></i>
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon" title="Ver Histórico de Alterações" @click="abrirAuditoria(row)">
            <i class="bi bi-clock-history text-secondary"></i>
          </button>
          <button class="btn-icon" @click="editarUsuario(row)" :title="temPermissaoCompleta ? 'Editar' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-primary' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="Usuario"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    :dicionarios="{'statusUsuario': listas.LstStatusUsuario, 'status': listas.LstStatusUsuario}"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
.filtros-titulo {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
}
</style>
